/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { matchesQuery } from "../../features/application-type/application-type-filter"
import type { ApplicationType } from "../../features/application-type/application-type-types"
import { valueOrDash } from "../../utils/value-or-dash"
import { CategoryPlatform } from "../components/category-platform"
import { Button } from "../primitives/button"
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue
} from "../primitives/combobox"

interface Props {
    items: ApplicationType[]
    selectedId: number
    /** Receives the chosen app type id, which the API types as optional. */
    onSelect: (selectedId: ApplicationType["id"]) => void
}

export const ApplicationTypePicker = ({ items, onSelect, selectedId }: Props) => {
    const selected = items.find((item) => item.id === selectedId) ?? null

    // Initialization loads the current user's app types before this renders, so
    // an empty list means there are none, not that they are still loading.
    const hasItems = items.length > 0
    const placeholder = hasItems ? "Select an application" : "No applications"

    return (
        <Combobox
            items={items}
            value={selected}
            onValueChange={(item: ApplicationType | null) => item && onSelect(item.id)}
            isItemEqualToValue={(item: ApplicationType, value: ApplicationType) => item.id === value.id}
            itemToStringLabel={(item: ApplicationType) => valueOrDash(item.description)}
            filter={matchesQuery}
            disabled={!hasItems}
        >
            <ComboboxTrigger
                // Keep the trigger height stable when the selected label changes.
                render={<Button variant="outline" className="w-full min-h-13 justify-between text-left font-normal" />}
                aria-label="Application"
            >
                <ComboboxValue>
                    {(item: ApplicationType | null) => (item ? <ItemView item={item} /> : placeholder)}
                </ComboboxValue>
            </ComboboxTrigger>
            <ComboboxContent
                align="center"
                // Match the popup width to the trigger instead of using its default minimum width.
                // The input's height is set here, not on `ComboboxInput`: the popup sizes its
                // input group from the parent, which outranks a class on the group itself.
                className="min-w-(--anchor-width) *:data-[slot=input-group]:h-10"
            >
                <ComboboxInput showTrigger={false} placeholder="Type to filter..." aria-label="Filter applications" />
                <ComboboxEmpty>No item found. Try to change the filter.</ComboboxEmpty>
                <ComboboxList>
                    {(item: ApplicationType) => (
                        <ComboboxItem key={item.id} value={item} showIndicator={false}>
                            <ItemView item={item} />
                        </ComboboxItem>
                    )}
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    )
}

const ItemView = ({ item }: { item: ApplicationType }) => (
    <span className="grid grid-cols-[minmax(0,1fr)_min-content] flex-auto items-center">
        <span className="flex flex-col">
            <strong className="truncate text-base font-medium leading-none">{valueOrDash(item.description)}</strong>
            <span className="truncate text-sm text-muted-foreground">{item.sourcePackageName}</span>
        </span>
        <CategoryPlatform category={item.category} platform={item.platform} />
    </span>
)
